import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';

import type {
  Properties,
  Properties_Insert_Input,
} from '@graphql/__generated__/graphql';
import { GET_PROPERTY_DUPLICATION_DATA } from '@modules/_shared/common/queries';
import {
  type NonObjectFields,
  getAddAllSchemaFieldsLink,
} from '@modules/_shared/utils/schema-introspection';

const addAllAvailableFieldsLink = getAddAllSchemaFieldsLink([
  'query_root',
  'leads',
  'properties',
  'property_images',
  'property_files',
]);

export const useDuplicatePropertyData = (options?: {
  onError?: (error: ErrorLike, clientOptions?: Record<string, unknown>) => void;
}): [
  (leadId: string) => Promise<Properties_Insert_Input | null>,
  { error: ErrorLike | null; loading: boolean },
] => {
  const [error, setError] = useState<ErrorLike | null>(null);
  const onError = options?.onError;

  const [
    getPropertyDuplicationData,
    { loading: loadingPropertyDuplicationData },
  ] = useLazyQuery(GET_PROPERTY_DUPLICATION_DATA, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const duplicateProperty = useCallback(
    async (leadId: string) => {
      const {
        data: propertyDuplicationData,
        error: propertyDuplicationDataError,
      } = await getPropertyDuplicationData({
        variables: { id: leadId },
        context: {
          customLink: addAllAvailableFieldsLink,
        },
      });

      if (propertyDuplicationDataError != null) {
        // Otherwise we overwrite the Apollo error with our own error.
        return null;
      }

      if (propertyDuplicationData?.leads_by_pk?.property == null) {
        (onError ?? setError)(new Error('Lead property not found'));

        return null;
      }

      const { property_files, property_images, ...propertyData } =
        propertyDuplicationData.leads_by_pk
          .property as unknown as NonObjectFields<
          Properties,
          'property_files' | 'property_images'
        >;

      const keysToSkip = [
        'borrower_lead_id',
        'centroid',
        'computed_surface',
        'created_at',
        'created_by',
        'deleted_by_user',
        'id',
        'latest_appraisal_id',
        'session_id',
        'source_property_id',
        'tenant_id',
        'updated_at',
      ];

      const propertyInput: Properties_Insert_Input = Object.entries(
        propertyData,
      ).reduce((acc, [key, value]) => {
        if (!keysToSkip.includes(key)) {
          acc[key as keyof Properties_Insert_Input] = value;
        }
        return acc;
      }, {} as Properties_Insert_Input);

      if (property_files != null && property_files.length > 0) {
        propertyInput.property_files = {
          data: property_files.map(propertyFile => ({
            ...propertyFile,
            id: undefined,
            property_id: undefined,
            tenant_id: undefined,
            created_at: undefined,
            updated_at: undefined,
          })),
        };
      }

      if (property_images != null && property_images.length > 0) {
        propertyInput.property_images = {
          data: property_images
            .sort((a, b) => a.order_nr - b.order_nr)
            .map(propertyImage => ({
              ...propertyImage,
              id: undefined,
              created_at: undefined,
              updated_at: undefined,
              property_id: undefined,
              watermarked_url: undefined,
            })),
        };
      }

      propertyInput.source_property_id = propertyData.id;

      return propertyInput;
    },
    [getPropertyDuplicationData, setError, onError],
  );

  return [
    duplicateProperty,
    { error, loading: loadingPropertyDuplicationData },
  ];
};
